import React, { useEffect, useMemo } from "react";
import { Box, Container, Tabs, Tab, Paper, Grid } from "@mui/material";
import PageTitle from "../components/common/PageTitle";
import ManageFaqs from "../components/ManageFaqs";
import ManageApis from "../components/ManageApis";
import ManageLinks from "../components/ManageLinks";
import ManageDocuments from "../components/ManageDocuments";
import ManageCategories from "../components/ManageCategories";
import ManageProducts from "../components/ManageProducts";
import ApiIcon from "@mui/icons-material/Api";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionIcon from "@mui/icons-material/Description";
import CategoryIcon from "@mui/icons-material/Category";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  tabIndexToSlug,
  tabSlugToIndex,
} from "../utils/manageAssetsTabs";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  if (value !== index) return null;
  return <Box sx={{ p: 3 }}>{children}</Box>;
};

const ManageAssetsPage: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabIndex = useMemo(() => {
    const tab = searchParams.get("tab");
    if (tab) return tabSlugToIndex(tab);
    if (location.state?.tabIndex != null) {
      return location.state.tabIndex as number;
    }
    return 0;
  }, [searchParams, location.state]);

  useEffect(() => {
    if (!searchParams.get("tab") && location.state?.tabIndex != null) {
      setSearchParams(
        { tab: tabIndexToSlug(location.state.tabIndex as number) },
        { replace: true },
      );
    }
  }, [location.state, searchParams, setSearchParams]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSearchParams({ tab: tabIndexToSlug(newValue) }, { replace: true });
  };

  const tabs = [
    { label: "APIs", icon: <ApiIcon fontSize="small" /> },
    { label: "FAQs", icon: <QuestionAnswerIcon fontSize="small" /> },
    { label: "Links", icon: <LinkIcon fontSize="small" /> },
    { label: "Documents", icon: <DescriptionIcon fontSize="small" /> },
    { label: "Categories", icon: <CategoryIcon fontSize="small" /> },
    { label: "Products", icon: <InventoryIcon fontSize="small" /> },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="Manage Assets"
        pageDescription="The links in left navigation will allow you to onboard and link your product entities (APIs, FAQs, Links, Documents, Categories) with Products."
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
                position: "sticky",
                top: 100,
              }}
            >
              <Tabs
                orientation="vertical"
                value={tabIndex}
                onChange={handleTabChange}
                variant="scrollable"
                sx={{
                  "& .MuiTab-root": {
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    textTransform: "none",
                    fontWeight: 500,
                    px: 3,
                    py: 0.75,
                    lineHeight: 1.3,
                    borderBottom: "1px solid",
                    borderColor: "grey.200",
                  },
                  "& .Mui-selected": {
                    fontWeight: 700,
                  },
                  "& .MuiTabs-flexContainer": {
                    flexDirection: "column",
                  },
                }}
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.label}
                    icon={tab.icon}
                    iconPosition="start"
                    label={tab.label}
                  />
                ))}
              </Tabs>
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
                minHeight: 400,
              }}
            >
              <TabPanel value={tabIndex} index={0}>
                <ManageApis />
              </TabPanel>
              <TabPanel value={tabIndex} index={1}>
                <ManageFaqs />
              </TabPanel>
              <TabPanel value={tabIndex} index={2}>
                <ManageLinks />
              </TabPanel>
              <TabPanel value={tabIndex} index={3}>
                <ManageDocuments />
              </TabPanel>
              <TabPanel value={tabIndex} index={4}>
                <ManageCategories />
              </TabPanel>
              <TabPanel value={tabIndex} index={5}>
                <ManageProducts />
              </TabPanel>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ManageAssetsPage;
